import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Badge,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Checkbox,
  Field,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PersonAdd24Regular } from '@fluentui/react-icons';
import {
  getAdminUsers,
  getAdminPermissions,
  setUserPermissions,
  type UserPermissionSummary,
  type PermissionInfo,
} from '../../api/adminClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  headerActions: { display: 'flex', gap: tokens.spacingHorizontalS },
  perms: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  checks: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginTop: tokens.spacingVerticalS },
  dialogError: { marginTop: tokens.spacingVerticalS },
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Admin > User Management. Lists every user holding at least one grant with their permission set,
 * and lets an admin add a new user (grant an email a permission set) or edit an existing user's
 * grants. Add/Edit share one dialog and post to the existing
 * PUT /api/v1/admin/users/{email}/permissions endpoint (an upsert keyed by email).
 */
export function UserManagementPage() {
  const styles = useStyles();
  const [users, setUsers] = useState<UserPermissionSummary[]>([]);
  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add/Edit dialog state.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExisting, setEditingExisting] = useState(false);
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const getAuthArgs = useAuthArgs();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage users.');
        return;
      }
      const [userList, perms] = await Promise.all([getAdminUsers(auth), getAdminPermissions(auth)]);
      setUsers(userList);
      setPermissions(perms);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuthArgs]);

  useEffect(() => {
    void load();
  }, [load]);

  const existingEmails = useMemo(
    () => new Set(users.map((u) => u.email.trim().toLowerCase())),
    [users],
  );

  const openAdd = () => {
    setEditingExisting(false);
    setEmail('');
    setSelected(new Set());
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (u: UserPermissionSummary) => {
    setEditingExisting(true);
    setEmail(u.email);
    setSelected(new Set(u.permissions));
    setDialogError(null);
    setDialogOpen(true);
  };

  const toggle = (name: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });
  };

  const save = useCallback(async () => {
    const target = email.trim().toLowerCase();
    if (!EMAIL_RE.test(target)) {
      setDialogError('Enter a valid email address.');
      return;
    }
    if (!editingExisting && existingEmails.has(target)) {
      setDialogError('That user already exists — edit them from the list instead.');
      return;
    }
    setSaving(true);
    setDialogError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setDialogError('You must be signed in to save.');
        return;
      }
      const result = await setUserPermissions(auth, { email: target, permissions: [...selected] });
      setNotice(
        editingExisting
          ? `Updated ${target} — ${result.length} permission(s).`
          : `Added ${target} with ${result.length} permission(s).`,
      );
      setDialogOpen(false);
      await load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [email, selected, editingExisting, existingEmails, load, getAuthArgs]);

  return (
    <section>
      <div className={styles.header}>
        <Text as="h1" size={600} weight="semibold">User Management</Text>
        <div className={styles.headerActions}>
          <Button appearance="primary" icon={<PersonAdd24Regular />} onClick={openAdd}>Add user</Button>
          <Button appearance="secondary" onClick={() => void load()}>Refresh</Button>
        </div>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {notice && (
        <MessageBar intent="success" style={{ marginBottom: 16 }}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <Spinner label="Loading users…" />
      ) : (
        <Table aria-label="Users and their permissions">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Permissions</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Text italic>No users have been granted permissions yet. Use “Add user” to grant access.</Text>
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.email}>
                  <TableCell>
                    <TableCellLayout>{u.email}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <div className={styles.perms}>
                      {u.permissions.length === 0 ? (
                        <Text italic size={200}>No permissions</Text>
                      ) : (
                        u.permissions.map((p) => (
                          <Badge key={p} appearance="tint" color="brand">{p}</Badge>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button size="small" appearance="subtle" onClick={() => openEdit(u)}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={(_, data) => setDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{editingExisting ? `Edit ${email}` : 'Add user'}</DialogTitle>
            <DialogContent>
              <Field label="User email" required>
                <Input
                  value={email}
                  placeholder="user@microsoft.com"
                  disabled={editingExisting}
                  onChange={(_, data) => setEmail(data.value)}
                />
              </Field>

              <Text size={300} weight="semibold" style={{ display: 'block', marginTop: 12 }}>Permissions</Text>
              <div className={styles.checks}>
                {permissions.length === 0 ? (
                  <Text italic>No permissions defined.</Text>
                ) : (
                  permissions.map((p) => (
                    <Checkbox
                      key={p.name}
                      checked={selected.has(p.name)}
                      onChange={(_, data) => toggle(p.name, data.checked === true)}
                      label={p.description ? `${p.name} — ${p.description}` : p.name}
                    />
                  ))
                )}
              </div>

              {dialogError && (
                <MessageBar intent="error" className={styles.dialogError}>
                  <MessageBarBody>{dialogError}</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button
                appearance="primary"
                onClick={() => void save()}
                disabled={saving || !email.trim()}
              >
                {saving ? 'Saving…' : editingExisting ? 'Save changes' : 'Add user'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}
